import React, { useState, useCallback, useRef } from "react";
import { searchContent } from "../api";

const USE_MOCK = false;

function mockSearch(query) {
  const q = query.toLowerCase();
  const notes = MOCK_NOTES.filter(
    (n) => n.title.toLowerCase().includes(q) || n.description.toLowerCase().includes(q)
  );
  const videos = MOCK_VIDEOS.filter(
    (v) => v.title.toLowerCase().includes(q) || v.chapter.toLowerCase().includes(q)
  );
  return { notes, videos };
}

export default function SearchPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 300));
        setResults(mockSearch(q));
      } else {
        const res = await searchContent(q);
        setResults(res);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const totalResults = results ? results.notes.length + results.videos.length : 0;

  return (
    <div style={s.page}>
      {/* Search bar */}
      <div style={s.searchBox}>
        <div style={s.searchLabel}>Search notes & video lectures</div>
        <div style={s.inputRow}>
          <span style={s.searchIcon}>🔍</span>
          <input
            style={s.input}
            value={query}
            onChange={handleInput}
            placeholder="e.g. Real Numbers, Polynomials, Chapter 3..."
            autoFocus
          />
          {query && (
            <button style={s.clearBtn} onClick={() => { setQuery(""); setResults(null); }}>✕</button>
          )}
        </div>
      </div>

      {/* Status */}
      {loading && <div style={s.status}>Searching…</div>}
      {!loading && results && (
        <div style={s.status}>
          {totalResults === 0
            ? `No results for "${query}"`
            : `${totalResults} result${totalResults > 1 ? "s" : ""} for "${query}"`}
        </div>
      )}
      {!loading && !results && (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📚</div>
          <div style={s.emptyText}>Type above to search across all notes and videos</div>
          <div style={s.tips}>
            {["Real Numbers", "Polynomials", "Chapter 3", "Linear Equations"].map((tip) => (
              <button key={tip} style={s.tipChip} onClick={() => { setQuery(tip); doSearch(tip); }}>{tip}</button>
            ))}
          </div>
        </div>
      )}

      {/* Notes results */}
      {results && results.notes.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>📄 Notes ({results.notes.length})</div>
          <div style={s.noteList}>
            {results.notes.map((note) => (
              <div key={note.id} style={s.noteCard}>
                <div style={s.pdfBadge}>PDF</div>
                <div style={s.noteInfo}>
                  <div style={s.noteTitle}>{highlight(note.title, query)}</div>
                  <div style={s.noteSub}>{note.description}</div>
                  <div style={s.noteMeta}>
                    <span style={s.sizeBadge}>{note.fileSize}</span>
                    <span style={s.metaDot}>·</span>
                    <span style={s.metaDate}>
                      {new Date(note.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                </div>
                <a href={note.fileUrl} download style={s.dlBtn}
                  onClick={(e) => { if (note.fileUrl === "#") { e.preventDefault(); alert("Connect backend to enable downloads."); } }}>
                  ⬇ Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video results */}
      {results && results.videos.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>🎥 Videos ({results.videos.length})</div>
          {activeVideo && (
            <div style={s.playerBox}>
              <div style={s.playerRatio}>
                <iframe
                  style={s.iframe}
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0`}
                  title={activeVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div style={s.playerMeta}>
                <span style={s.chBadge}>{activeVideo.chapter}</span>
                <span style={s.playerTitle}>{activeVideo.title}</span>
              </div>
            </div>
          )}
          <div style={s.videoGrid}>
            {results.videos.map((v) => (
              <div key={v.id} style={{ ...s.videoCard, ...(activeVideo?.id === v.id ? s.videoCardActive : {}) }}
                onClick={() => setActiveVideo(v)}>
                <div style={s.thumbBox}>
                  <img src={`https://img.youtube.com/vi/${v.youtubeId}/mqdefault.jpg`} alt={v.title} style={s.thumbImg} />
                  <div style={s.playOverlay}>▶</div>
                </div>
                <div style={s.videoMeta}>
                  <div style={s.videoTitle}>{highlight(v.title, query)}</div>
                  <div style={s.videoDur}><span style={s.chTag}>{v.chapter}</span> ⏱ {v.duration}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {results && totalResults === 0 && (
        <div style={s.noResults}>
          <div style={{ fontSize: 40 }}>🔎</div>
          <div style={{ marginTop: 12, color: "#64748b", fontFamily: "'Sora',sans-serif" }}>No results found. Try different keywords.</div>
        </div>
      )}
    </div>
  );
}

function highlight(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "#854d0e", color: "#fef3c7", borderRadius: 3, padding: "0 2px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

const s = {
  page: { maxWidth: 900, margin: "0 auto", padding: "40px 20px 80px" },
  searchBox: { marginBottom: 24 },
  searchLabel: { fontSize: 22, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Sora',sans-serif", marginBottom: 16 },
  inputRow: { display: "flex", alignItems: "center", background: "#1e293b", border: "2px solid #334155", borderRadius: 14, padding: "0 16px", gap: 10 },
  searchIcon: { fontSize: 18, flexShrink: 0 },
  input: { flex: 1, background: "transparent", border: "none", outline: "none", color: "#f1f5f9", fontSize: 16, padding: "16px 0", fontFamily: "'Sora',sans-serif" },
  clearBtn: { background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 16, padding: 4 },
  status: { fontSize: 13, color: "#64748b", fontFamily: "'Sora',sans-serif", marginBottom: 20 },
  empty: { textAlign: "center", padding: "60px 0" },
  emptyIcon: { fontSize: 48, marginBottom: 12 },
  emptyText: { color: "#64748b", fontSize: 15, fontFamily: "'Sora',sans-serif", marginBottom: 20 },
  tips: { display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" },
  tipChip: { background: "#1e293b", border: "1px solid #334155", color: "#94a3b8", borderRadius: 100, padding: "6px 16px", fontSize: 13, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  section: { marginBottom: 36 },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: "#e2e8f0", fontFamily: "'Sora',sans-serif", marginBottom: 14 },
  noteList: { display: "flex", flexDirection: "column", gap: 10 },
  noteCard: { display: "flex", alignItems: "center", gap: 14, background: "#1e293b", border: "1px solid #334155", borderRadius: 12, padding: "14px 16px", flexWrap: "wrap" },
  pdfBadge: { background: "#ef4444", color: "#fff", fontSize: 9, fontWeight: 800, borderRadius: 6, padding: "5px 7px", fontFamily: "monospace", flexShrink: 0 },
  noteInfo: { flex: 1, minWidth: 0 },
  noteTitle: { fontSize: 14, fontWeight: 600, color: "#f1f5f9", fontFamily: "'Sora',sans-serif" },
  noteSub: { fontSize: 12, color: "#64748b", marginTop: 2, fontFamily: "'Sora',sans-serif" },
  noteMeta: { display: "flex", alignItems: "center", gap: 6, marginTop: 5 },
  sizeBadge: { fontSize: 10, background: "#0f172a", color: "#64748b", borderRadius: 4, padding: "2px 6px", fontFamily: "monospace" },
  metaDot: { color: "#334155", fontSize: 12 },
  metaDate: { fontSize: 11, color: "#475569", fontFamily: "'Sora',sans-serif" },
  dlBtn: { background: "#3b82f6", color: "#fff", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap", fontFamily: "'Sora',sans-serif", flexShrink: 0 },
  playerBox: { background: "#0f172a", border: "1px solid #334155", borderRadius: 14, overflow: "hidden", marginBottom: 16 },
  playerRatio: { position: "relative", paddingTop: "56.25%" },
  iframe: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%" },
  playerMeta: { padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 },
  chBadge: { fontSize: 10, background: "#1d4ed8", color: "#bfdbfe", borderRadius: 6, padding: "3px 8px", fontFamily: "'Sora',sans-serif", fontWeight: 600, whiteSpace: "nowrap" },
  playerTitle: { fontSize: 14, fontWeight: 600, color: "#f1f5f9", fontFamily: "'Sora',sans-serif" },
  videoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 },
  videoCard: { background: "#1e293b", border: "1px solid #334155", borderRadius: 12, overflow: "hidden", cursor: "pointer" },
  videoCardActive: { border: "1.5px solid #3b82f6" },
  thumbBox: { position: "relative", paddingTop: "56.25%", background: "#0f172a" },
  thumbImg: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" },
  playOverlay: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)", color: "#fff", fontSize: 22 },
  videoMeta: { padding: "10px 12px" },
  videoTitle: { fontSize: 13, fontWeight: 600, color: "#e2e8f0", fontFamily: "'Sora',sans-serif", marginBottom: 4 },
  videoDur: { fontSize: 11, color: "#64748b", fontFamily: "'Sora',sans-serif", display: "flex", alignItems: "center", gap: 6 },
  chTag: { fontSize: 10, background: "#0f172a", color: "#475569", borderRadius: 4, padding: "2px 5px" },
  noResults: { textAlign: "center", padding: "60px 0" },
};
